import React, { useEffect, useMemo, useState, lazy, memo } from 'react';
import { Search, MapPin, Briefcase, Settings, Filter, X, ChevronDown, UserPlus, HelpCircle, Zap, Loader, CheckCircle2, Info, Home, Users, Globe, Calendar, MessageSquare, CheckCircle, Clock, AlertCircle, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Memorized connection card component for better performance
const ConnectionCard = memo(({
  connection,
  onShowMatchInfo,
  onProfileClick
}) => {
  return <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm" onClick={() => onProfileClick(connection.name)} data-id="element-198">
        <div className="flex flex-col items-center text-center" data-id="element-199">
          <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-3 border-2 border-gray-200" data-id="element-200">
            <img src={connection.image} alt={connection.name} className="w-full h-full object-cover" data-id="element-201" />
          </div>
          <h3 className="font-sharp-grotesk text-sm font-medium mb-1 text-black" data-id="element-202">
            {connection.name}
          </h3>
          <p className="text-xs text-gray-500 font-medium mb-2" data-id="element-203">
            {connection.role}
          </p>
          <div className="flex items-center text-xs text-gray-500 mb-2" data-id="element-204">
            <MapPin className="w-3 h-3 mr-1" data-id="element-205" />
            {connection.location}
          </div>
          <div className="relative inline-block mb-3" data-id="element-206">
            <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full inline-flex items-center font-medium border border-gray-200" data-id="element-207">
              <span data-id="element-208">{connection.match} match</span>
              <button className="ml-1 inline-flex" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onShowMatchInfo({
              show: true,
              name: connection.name,
              match: connection.match,
              closing: false
            });
          }} data-id="element-209">
                <HelpCircle className="w-3 h-3 text-gray-500" data-id="element-210" />
              </button>
            </span>
          </div>
          <button className={`w-full py-1.5 rounded-lg text-xs font-medium ${connection.connected ? 'border border-gray-300 text-gray-700' : 'bg-black text-white'}`} onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }} data-id="element-211">
            {connection.connected ? 'Already connected' : <>
                <UserPlus className="w-3 h-3 inline-block mr-1" data-id="element-212" />
                Connect
              </>}
          </button>
        </div>
      </div>;
});
export function ConnectionsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'connected' | 'board' | 'requests'>('all');
  const [filters, setFilters] = useState({
    location: '',
    role: '',
    skills: [],
    availability: 'all'
  });
  const [showMatchInfo, setShowMatchInfo] = useState<{
    show: boolean;
    name: string;
    match: string;
    closing: boolean;
  }>({
    show: false,
    name: '',
    match: '',
    closing: false
  });
  // Add explanation modal state
  const [showMatchExplanation, setShowMatchExplanation] = useState(false);
  // Add loading state
  const [isLoading, setIsLoading] = useState(true);
  // Add matching state
  const [isMatching, setIsMatching] = useState(false);
  const [matchingCompleted, setMatchingCompleted] = useState(false);
  // Add matching type state
  const [matchingType, setMatchingType] = useState<string>('professional');
  // Function to handle profile click and navigate to profile page
  const handleProfileClick = username => {
    // Convert the username to a URL-friendly format (replace spaces with dashes, lowercase)
    const formattedUsername = username.toLowerCase().replace(/\s+/g, '-');
    navigate(`/profile/${formattedUsername}`);
  };
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  const locations = ['Rome', 'Milan', 'Florence', 'Turin', 'Naples'];
  const roles = ['UX/UI Designer', 'Web Developer', 'Product Manager', 'Marketing', 'Data Scientist'];
  const skills = ['Design', 'Development', 'Marketing', 'Data Analysis', 'Project Management', 'Content Creation', 'Sales', 'Customer Service'];
  const handleFilterChange = (key: string, value: any) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };
  const handleSkillToggle = (skill: string) => {
    if (filters.skills.includes(skill)) {
      setFilters({
        ...filters,
        skills: filters.skills.filter(s => s !== skill)
      });
    } else {
      setFilters({
        ...filters,
        skills: [...filters.skills, skill]
      });
    }
  };
  const resetFilters = () => {
    setFilters({
      location: '',
      role: '',
      skills: [],
      availability: 'all'
    });
  };
  const closeFiltersModal = () => {
    setShowFilters(false);
  };
  // Function to handle match & connect
  const handleMatchAndConnect = () => {
    setIsMatching(true);
    setMatchingCompleted(false);
    // Simulate algorithm processing
    setTimeout(() => {
      setIsMatching(false);
      setMatchingCompleted(true);
      // Reset matchingCompleted after 3 seconds
      setTimeout(() => {
        setMatchingCompleted(false);
      }, 3000);
    }, 2000);
  };
  const connections = [{
    id: 1,
    name: 'Giulia Verdi',
    role: 'Web Developer',
    location: 'Rome',
    match: '92%',
    connected: true,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg',
    skills: ['Development', 'Design']
  }, {
    id: 2,
    name: 'Roberto Neri',
    role: 'Marketing',
    location: 'Milan',
    match: '78%',
    connected: true,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/37RNAboQN8qk3BnTK1YzhT/irene-strong-TMt3JGoVlng-unsplash.jpg',
    skills: ['Marketing', 'Content Creation']
  }, {
    id: 3,
    name: 'Marco Bianchi',
    role: 'UX Designer',
    location: 'Rome',
    match: '85%',
    connected: true,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg',
    skills: ['Design', 'Project Management']
  }, {
    id: 4,
    name: 'Laura Rossi',
    role: 'Product Manager',
    location: 'Florence',
    match: '90%',
    connected: true,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg',
    skills: ['Project Management', 'Marketing']
  }, {
    id: 5,
    name: 'Andrea Marino',
    role: 'Data Scientist',
    location: 'Turin',
    match: '82%',
    connected: true,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg',
    skills: ['Data Analysis', 'Development']
  }, {
    id: 6,
    name: 'Francesca Ricci',
    role: 'Content Creator',
    location: 'Naples',
    match: '75%',
    connected: false,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg',
    skills: ['Content Creation', 'Marketing']
  }, {
    id: 7,
    name: 'Davide Conti',
    role: 'Frontend Developer',
    location: 'Milan',
    match: '88%',
    connected: false,
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    skills: ['Development', 'Design']
  }, {
    id: 8,
    name: 'Elena Martini',
    role: 'UX Researcher',
    location: 'Rome',
    match: '93%',
    connected: false,
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    skills: ['Design', 'Data Analysis']
  }];
  // Sample collaboration board data
  const collaborationBoard = [{
    id: 1,
    author: {
      name: 'Marco Bianchi',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg'
    },
    title: 'Looking for frontend developer for e-commerce project',
    description: 'I am working on an e-commerce project in the fashion industry and I am looking for a frontend developer with React experience to collaborate on the website. The project will last about 3 months.',
    skills: ['React', 'Frontend', 'E-commerce'],
    postedAt: '2 days ago'
  }, {
    id: 2,
    author: {
      name: 'Laura Rossi',
      role: 'Product Manager',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg'
    },
    title: 'UX/UI Designer for fintech startup',
    description: 'Our fintech startup is looking for a UX/UI Designer to redesign our mobile app interface. We are looking for someone with experience in the financial sector.',
    skills: ['UX/UI', 'Mobile', 'Fintech'],
    postedAt: '3 days ago'
  }, {
    id: 3,
    author: {
      name: 'Andrea Marino',
      role: 'Data Scientist',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg'
    },
    title: 'Collaboration for data analysis project',
    description: 'I am working on a data analysis project in the retail sector and I am looking for a data analyst with experience in Python and data visualization to collaborate part-time.',
    skills: ['Python', 'Data Analysis', 'Visualization'],
    postedAt: '1 week ago'
  }];
  // Sample connection requests
  const connectionRequests = [{
    id: 1,
    user: {
      name: 'Davide Conti',
      role: 'Frontend Developer',
      location: 'Milan',
      image: 'https://randomuser.me/api/portraits/men/32.jpg'
    },
    message: 'Hi! I would like to connect with you to discuss possible collaborations in frontend development.',
    sentAt: '2 days ago'
  }, {
    id: 2,
    user: {
      name: 'Elena Martini',
      role: 'UX Researcher',
      location: 'Rome',
      image: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    message: 'I saw your profile and I am interested in connecting to exchange ideas on design thinking and user research.',
    sentAt: '3 days ago'
  }, {
    id: 3,
    user: {
      name: 'Francesca Ricci',
      role: 'Content Creator',
      location: 'Naples',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    },
    message: 'Hi! I am a content creator specialized in digital marketing. I would like to connect for future collaborations.',
    sentAt: '1 week ago'
  }];
  // Use useMemo for filtering connections
  const filteredConnections = useMemo(() => {
    return connections.filter(connection => {
      // Filter by search term
      if (searchTerm && !connection.name.toLowerCase().includes(searchTerm.toLowerCase()) && !connection.role.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Filter by tab (all or connected)
      if (activeTab === 'connected' && !connection.connected) {
        return false;
      }
      // Filter by location
      if (filters.location && connection.location !== filters.location) {
        return false;
      }
      // Filter by role
      if (filters.role && connection.role !== filters.role) {
        return false;
      }
      // Filter by skills
      if (filters.skills.length > 0 && !connection.skills.some(skill => filters.skills.includes(skill))) {
        return false;
      }
      return true;
    });
  }, [searchTerm, activeTab, filters, connections]);
  return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans" data-id="element-213">
      {/* Main content */}
      <div className="flex-grow bg-white pt-6 pb-20 md:pb-10" data-id="element-214">
        <div className="max-w-6xl mx-auto px-0" data-id="element-215">
          {/* Title row with filter button on the right */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-216">
            <div className="flex justify-between items-center w-full md:w-auto mb-4 md:mb-0" data-id="element-217">
              <h1 className="text-2xl md:text-3xl font-light font-sharp-grotesk" data-id="element-218">
                My Connections
              </h1>
            </div>
            <div className="w-full md:w-auto overflow-x-auto pb-1" data-id="element-219">
              <div className="border-b border-gray-200 mb-4" data-id="element-220">
                <div className="flex -mb-px" data-id="element-221">
                  <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'all' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('all')} data-id="element-222">
                    All Profiles
                  </button>
                  <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'connected' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('connected')} data-id="element-223">
                    Connected
                  </button>
                  <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'requests' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('requests')} data-id="element-224">
                    Requests
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Search bar row with filter button next to it */}
          <div className="mb-6 flex items-center gap-3" data-id="element-225">
            <div className="relative flex-grow" data-id="element-226">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-227">
                <Search className="h-5 w-5 text-gray-400" data-id="element-228" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="Search by name, role or skills..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-229" />
            </div>
            <button className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg" onClick={() => setShowFilters(true)} data-id="element-230">
              <Filter className="h-5 w-5 mr-2" data-id="element-231" />
              <span data-id="element-232">Filters</span>
            </button>
          </div>
          {/* Centralized Match & Connect section with explanation button - only shown for "all" tab */}
          {activeTab === 'all' && <div className="mb-8 bg-gray-50 rounded-lg p-6 text-center border border-gray-200" data-id="element-233">
              <div className="flex flex-col items-center" data-id="element-234">
                <div className="flex items-center mb-2" data-id="element-235">
                  <Zap className="h-5 w-5 text-yellow-500 mr-2" data-id="element-236" />
                  <h2 className="text-xl font-medium font-sharp-grotesk" data-id="element-237">
                    Match & Connect
                  </h2>
                  <button className="ml-2 text-gray-500" onClick={() => setShowMatchExplanation(true)} data-id="element-238">
                    <Info className="h-5 w-5" data-id="element-239" />
                  </button>
                </div>
                <p className="text-gray-600 text-sm mb-4 max-w-lg" data-id="element-240">
                  Find relevant connections based on your profile and
                  professional interests
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md" data-id="element-241">
                  <div className="relative flex-grow" data-id="element-242">
                    <select value={matchingType} onChange={e => setMatchingType(e.target.value)} className="appearance-none w-full bg-white border border-gray-300 text-gray-700 py-2.5 pl-3 pr-8 rounded-lg leading-tight focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" data-id="element-243">
                      <option value="professional" data-id="element-244">For work</option>
                      <option value="collaboration" data-id="element-245">For collaborations</option>
                      <option value="networking" data-id="element-246">For networking</option>
                      <option value="mentorship" data-id="element-247">For mentorship</option>
                      <option value="learning" data-id="element-248">For learning</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700" data-id="element-249">
                      <ChevronDown className="h-4 w-4" data-id="element-250" />
                    </div>
                  </div>
                  <button className="flex items-center justify-center px-4 py-2.5 bg-black text-white rounded-lg" onClick={handleMatchAndConnect} disabled={isMatching} data-id="element-251">
                    {isMatching ? <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" data-id="element-252" />
                        <span data-id="element-253">Matching...</span>
                      </> : <span data-id="element-254">Match & Connect</span>}
                  </button>
                </div>
                {matchingCompleted && <div className="mt-3 text-sm text-green-600 flex items-center" data-id="element-255">
                    <CheckCircle2 className="h-4 w-4 mr-1" data-id="element-256" />
                    Matching completed successfully!
                  </div>}
              </div>
            </div>}
          {/* Results count - moved here before the connections list */}
          {(activeTab === 'all' || activeTab === 'connected') && <div className="text-sm text-gray-500 mb-4" data-id="element-257">
              {filteredConnections.length} results found
            </div>}
          {/* Skeleton loader during loading */}
          {isLoading && (activeTab === 'all' || activeTab === 'connected') ? <div className="grid grid-cols-2 gap-4" data-id="element-258">
              {[1, 2, 3, 4].map(i => <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse" data-id="element-259"></div>)}
            </div> : <>
              {/* Content based on active tab */}
              {activeTab === 'all' && <div className="grid grid-cols-2 gap-4" data-id="element-260">
                  {filteredConnections.map(connection => <ConnectionCard key={connection.id} connection={connection} onShowMatchInfo={setShowMatchInfo} onProfileClick={handleProfileClick} data-id="element-261" />)}
                </div>}
              {activeTab === 'connected' && <div className="space-y-3" data-id="element-262">
                  {filteredConnections.map(connection => <div key={connection.id} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm" onClick={() => handleProfileClick(connection.name)} data-id="element-263">
                      <div className="flex items-center" data-id="element-264">
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0" data-id="element-265">
                          <img src={connection.image} alt={connection.name} className="w-full h-full object-cover" data-id="element-266" />
                        </div>
                        <div className="ml-4 flex-grow" data-id="element-267">
                          <h3 className="font-sharp-grotesk text-sm font-medium mb-1 text-black" data-id="element-268">
                            {connection.name}
                          </h3>
                          <p className="text-xs text-gray-500 font-medium mb-1" data-id="element-269">
                            {connection.role}
                          </p>
                          <div className="flex items-center text-xs text-gray-500" data-id="element-270">
                            <MapPin className="w-3 h-3 mr-1" data-id="element-271" />
                            {connection.location}
                          </div>
                        </div>
                        <div className="flex flex-col items-end ml-2" data-id="element-272">
                          <div className="relative inline-block mb-2" data-id="element-273">
                            <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full inline-flex items-center font-medium border border-gray-200" data-id="element-274">
                              <span data-id="element-275">{connection.match} match</span>
                              <button className="ml-1 inline-flex" onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowMatchInfo({
                          show: true,
                          name: connection.name,
                          match: connection.match,
                          closing: false
                        });
                      }} data-id="element-276">
                                <HelpCircle className="w-3 h-3 text-gray-500" data-id="element-277" />
                              </button>
                            </span>
                          </div>
                          <button className="py-1.5 px-3 rounded-lg text-xs font-medium border border-gray-300 text-gray-700" onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }} data-id="element-278">
                            Message
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>}
              {/* Board tab content */}
              {activeTab === 'board' && <div className="space-y-4" data-id="element-279">
                  <div className="flex justify-between items-center mb-2" data-id="element-280">
                    <h2 className="text-lg font-medium" data-id="element-281">
                      Collaboration Announcements
                    </h2>
                    <button className="px-4 py-2 bg-black text-white rounded-lg text-sm flex items-center" data-id="element-282">
                      <Plus className="w-4 h-4 mr-2" data-id="element-283" />
                      Post
                    </button>
                  </div>
                  {collaborationBoard.map(post => <div key={post.id} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group cursor-pointer" data-id="element-284">
                      <div className="flex items-start gap-4" data-id="element-285">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-286">
                          <img src={post.author.image} alt={post.author.name} className="w-full h-full object-cover" data-id="element-287" />
                        </div>
                        <div className="flex-1" data-id="element-288">
                          <div className="flex justify-between items-start mb-2" data-id="element-289">
                            <div data-id="element-290">
                              <h3 className="font-medium text-base mb-1" data-id="element-291">
                                {post.title}
                              </h3>
                              <div className="flex items-center text-xs text-gray-500 mb-2" data-id="element-292">
                                <span className="font-medium text-gray-700 mr-1" data-id="element-293">
                                  {post.author.name}
                                </span>
                                <span data-id="element-294">
                                  • {post.author.role} • {post.postedAt}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3" data-id="element-295">
                            {post.description}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-4" data-id="element-296">
                            {post.skills.map((skill, index) => <span key={index} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-297">
                                {skill}
                              </span>)}
                          </div>
                          <div className="flex gap-2" data-id="element-298">
                            <button className="bg-black text-white text-xs px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-299">
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" data-id="element-300" />
                              Contact
                            </button>
                            <button className="border border-gray-300 text-gray-700 text-xs px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-301">
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>}
              {/* Requests tab content */}
              {activeTab === 'requests' && <div className="space-y-4" data-id="element-302">
                  <div className="flex justify-between items-center mb-2" data-id="element-303">
                    <h2 className="text-lg font-medium" data-id="element-304">Connection Requests</h2>
                  </div>
                  {connectionRequests.map(request => <div key={request.id} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group cursor-pointer" data-id="element-305">
                      <div className="flex items-start gap-4" data-id="element-306">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-307">
                          <img src={request.user.image} alt={request.user.name} className="w-full h-full object-cover" data-id="element-308" />
                        </div>
                        <div className="flex-1" data-id="element-309">
                          <div className="flex justify-between items-start mb-2" data-id="element-310">
                            <div data-id="element-311">
                              <h3 className="font-medium text-base mb-1" data-id="element-312">
                                {request.user.name}
                              </h3>
                              <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-313">
                                <span data-id="element-314">{request.user.role}</span>
                                <span className="mx-1" data-id="element-315">•</span>
                                <MapPin className="w-3 h-3 mr-0.5" data-id="element-316" />
                                <span data-id="element-317">{request.user.location}</span>
                              </div>
                              <div className="flex items-center text-xs text-gray-500 mb-2" data-id="element-318">
                                <Clock className="w-3 h-3 mr-0.5" data-id="element-319" />
                                <span data-id="element-320">{request.sentAt}</span>
                              </div>
                            </div>
                            <div className="flex items-center" data-id="element-321">
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-322">
                                <AlertCircle className="w-3 h-3 mr-1" data-id="element-323" />
                                Pending
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3" data-id="element-324">
                            {request.message}
                          </p>
                          <div className="flex gap-2" data-id="element-325">
                            <button className="bg-black text-white text-xs px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-326">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5" data-id="element-327" />
                              Accept
                            </button>
                            <button className="border border-gray-300 text-gray-700 text-xs px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-328">
                              Decline
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>}
              {/* Empty state */}
              {filteredConnections.length === 0 && (activeTab === 'all' || activeTab === 'connected') && <div className="flex flex-col items-center justify-center p-6 border border-gray-100 rounded-lg" data-id="element-329">
                    <div className="text-4xl mb-4" data-id="element-330">🔍</div>
                    <h3 className="text-lg font-medium mb-2" data-id="element-331">
                      No results found
                    </h3>
                    <p className="text-gray-500 text-center mb-4 text-sm" data-id="element-332">
                      Try changing your filters or searching for another term.
                    </p>
                    <button className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm" onClick={resetFilters} data-id="element-333">
                      Reset filters
                    </button>
                  </div>}
            </>}
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 z-50" data-id="element-334">
        <button onClick={() => navigate('/')} className="flex flex-col items-center" data-id="element-335">
          <Home className="w-5 h-5 text-gray-400" data-id="element-336" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-337">Home</span>
        </button>
        <button className="flex flex-col items-center" data-id="element-338">
          <Users className="w-5 h-5 text-black" data-id="element-339" />
          <span className="text-xs mt-1 text-black" data-id="element-340">Connections</span>
        </button>
        <button onClick={() => navigate('/networking')} className="flex flex-col items-center" data-id="element-341">
          <Globe className="w-5 h-5 text-gray-400" data-id="element-342" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-343">Networking</span>
        </button>
        <button onClick={() => navigate('/collaboration')} className="flex flex-col items-center" data-id="element-344">
          <Briefcase className="w-5 h-5 text-gray-400" data-id="element-345" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-346">Collaboration</span>
        </button>
        <button onClick={() => navigate('/events')} className="flex flex-col items-center" data-id="element-347">
          <Calendar className="w-5 h-5 text-gray-400" data-id="element-348" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-349">Events</span>
        </button>
      </div>

      {/* Filters Modal */}
      {showFilters && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-350">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={closeFiltersModal} data-id="element-351"></div>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden transform transition-all duration-300 ease-out animate-slide-up mx-4" data-id="element-352">
            <div className="p-6" data-id="element-353">
              <div className="flex justify-between items-center mb-4" data-id="element-354">
                <h3 className="font-medium text-lg" data-id="element-355">Filter results</h3>
                <div className="flex items-center gap-3" data-id="element-356">
                  <button className="text-sm text-gray-500" onClick={resetFilters} data-id="element-357">
                    Reset filters
                  </button>
                  <button className="text-gray-500" onClick={closeFiltersModal} data-id="element-358">
                    <X className="h-5 w-5" data-id="element-359" />
                  </button>
                </div>
              </div>
              <div className="space-y-4" data-id="element-360">
                {/* Location filter */}
                <div data-id="element-361">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-362">
                    Location
                  </label>
                  <div className="relative" data-id="element-363">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.location} onChange={e => handleFilterChange('location', e.target.value)} data-id="element-364">
                      <option value="" data-id="element-365">All locations</option>
                      {locations.map(location => <option key={location} value={location} data-id="element-366">
                          {location}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-367">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-368" />
                    </div>
                  </div>
                </div>
                {/* Role filter */}
                <div data-id="element-369">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-370">
                    Role
                  </label>
                  <div className="relative" data-id="element-371">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.role} onChange={e => handleFilterChange('role', e.target.value)} data-id="element-372">
                      <option value="" data-id="element-373">All roles</option>
                      {roles.map(role => <option key={role} value={role} data-id="element-374">
                          {role}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-375">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-376" />
                    </div>
                  </div>
                </div>
                {/* Availability filter */}
                <div data-id="element-377">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-378">
                    Availability
                  </label>
                  <div className="relative" data-id="element-379">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.availability} onChange={e => handleFilterChange('availability', e.target.value)} data-id="element-380">
                      <option value="all" data-id="element-381">All</option>
                      <option value="available" data-id="element-382">Available</option>
                      <option value="busy" data-id="element-383">Busy</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-384">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-385" />
                    </div>
                  </div>
                </div>
                {/* Skills filter */}
                <div data-id="element-386">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-387">
                    Skills
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-388">
                    {skills.map(skill => <button key={skill} className={`px-3 py-1.5 text-sm rounded-full ${filters.skills.includes(skill) ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => handleSkillToggle(skill)} data-id="element-389">
                        {skill}
                      </button>)}
                  </div>
                </div>
                <button className="w-full mt-4 bg-black text-white py-2.5 rounded-lg hover:bg-gray-800" onClick={closeFiltersModal} data-id="element-390">
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Match & Connect Explanation Modal */}
      {showMatchExplanation && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-391">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={() => setShowMatchExplanation(false)} data-id="element-392"></div>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden transform transition-all duration-300 ease-out animate-slide-up mx-4" data-id="element-393">
            <div className="p-6" data-id="element-394">
              <div className="flex justify-between items-center mb-4" data-id="element-395">
                <h3 className="font-medium text-lg font-sharp-grotesk" data-id="element-396">
                  How Match & Connect works
                </h3>
                <button className="text-gray-500" onClick={() => setShowMatchExplanation(false)} data-id="element-397">
                  <X className="h-5 w-5" data-id="element-398" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-399">
                <div className="flex items-start gap-3" data-id="element-400">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-401">
                    <span className="text-white font-medium" data-id="element-402">1</span>
                  </div>
                  <div data-id="element-403">
                    <h4 className="font-medium mb-1" data-id="element-404">
                      Connection type selection
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-405">
                      Choose the type of connection you're looking for: work,
                      collaborations, networking, mentorship, or learning.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-406">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-407">
                    <span className="text-white font-medium" data-id="element-408">2</span>
                  </div>
                  <div data-id="element-409">
                    <h4 className="font-medium mb-1" data-id="element-410">Profile analysis</h4>
                    <p className="text-sm text-gray-600" data-id="element-411">
                      Our algorithm analyzes your profile, skills, interests,
                      and professional goals.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-412">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-413">
                    <span className="text-white font-medium" data-id="element-414">3</span>
                  </div>
                  <div data-id="element-415">
                    <h4 className="font-medium mb-1" data-id="element-416">Advanced matchmaking</h4>
                    <p className="text-sm text-gray-600" data-id="element-417">
                      We compare your profile with other community members to
                      find the most relevant connections.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-418">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-419">
                    <span className="text-white font-medium" data-id="element-420">4</span>
                  </div>
                  <div data-id="element-421">
                    <h4 className="font-medium mb-1" data-id="element-422">Personalized results</h4>
                    <p className="text-sm text-gray-600" data-id="element-423">
                      Receive connection suggestions with match percentages
                      based on professional compatibility.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-6 bg-gray-50 p-3 rounded-lg" data-id="element-424">
                  <Zap className="h-5 w-5 text-yellow-500" data-id="element-425" />
                  <p className="text-sm" data-id="element-426">
                    <span className="font-medium" data-id="element-427">Pro Tip:</span> Regularly
                    update your profile to improve the quality of your matches.
                  </p>
                </div>
              </div>
              <button className="w-full mt-6 bg-black text-white py-2.5 rounded-lg hover:bg-gray-800" onClick={() => setShowMatchExplanation(false)} data-id="element-428">
                Got it
              </button>
            </div>
          </div>
        </div>}

      {/* Match Info Modal */}
      {showMatchInfo.show && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" data-id="element-429">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={() => {
        setShowMatchInfo({
          ...showMatchInfo,
          closing: true
        });
        setTimeout(() => {
          setShowMatchInfo({
            show: false,
            name: '',
            match: '',
            closing: false
          });
        }, 300);
      }} data-id="element-430"></div>
          <div className={`bg-white w-full max-w-md rounded-t-xl sm:rounded-xl overflow-hidden transform transition-all duration-300 ease-out ${showMatchInfo.closing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-431">
            <div className="p-6" data-id="element-432">
              <div className="flex items-center justify-between mb-4" data-id="element-433">
                <h3 className="text-xl font-medium font-sharp-grotesk" data-id="element-434">
                  Why {showMatchInfo.match} with {showMatchInfo.name}?
                </h3>
                <button onClick={() => {
              setShowMatchInfo({
                ...showMatchInfo,
                closing: true
              });
              setTimeout(() => {
                setShowMatchInfo({
                  show: false,
                  name: '',
                  match: '',
                  closing: false
                });
              }, 300);
            }} className="text-gray-500" data-id="element-435">
                  <X className="w-5 h-5" data-id="element-436" />
                </button>
              </div>
              <h4 className="font-medium text-lg mb-3 font-sharp-grotesk" data-id="element-437">
                Why we matched you
              </h4>
              <ul className="space-y-4 mb-6" data-id="element-438">
                <li className="flex items-start" data-id="element-439">
                  <span className="mr-2" data-id="element-440">•</span>
                  <p className="text-sm" data-id="element-441">
                    <strong data-id="element-442">Role:</strong> {showMatchInfo.name} has a role
                    complementary to yours, which can lead to productive
                    collaborations.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-443">
                  <span className="mr-2" data-id="element-444">•</span>
                  <p className="text-sm" data-id="element-445">
                    <strong data-id="element-446">Industry:</strong> You both work in the consulting
                    industry, sharing specific market knowledge.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-447">
                  <span className="mr-2" data-id="element-448">•</span>
                  <p className="text-sm" data-id="element-449">
                    <strong data-id="element-450">Skills:</strong> You have similar skills in software
                    development.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-451">
                  <span className="mr-2" data-id="element-452">•</span>
                  <p className="text-sm" data-id="element-453">
                    <strong data-id="element-454">Company type:</strong> You both work in startups,
                    with similar organizational dynamics.
                  </p>
                </li>
              </ul>
              <h4 className="font-medium text-lg mb-3 font-sharp-grotesk" data-id="element-455">
                Key factors
              </h4>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-456">
                <span className="border border-black rounded-full px-4 py-1 text-sm" data-id="element-457">
                  Role
                </span>
                <span className="border border-black rounded-full px-4 py-1 text-sm" data-id="element-458">
                  Interests
                </span>
                <span className="border border-black rounded-full px-4 py-1 text-sm" data-id="element-459">
                  Skills
                </span>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Export as default for lazy()
export default {
  ConnectionsPage
};